import { apiClient } from '@/api/http/client'
import { ApiError, type ApiResponse } from '@/api/http/types'
import type { CampaignInstrument } from '@/features/campaigns/types'
import {
  readInstrumentWorkspaceContext,
  rememberInstrumentWorkspaceContext,
} from '@/features/instruments/lib/instrumentWorkspaceSession'

import {
  campaignInstrumentPath,
  instrumentApiPath,
} from '@/features/instruments/infra/instrumentPaths'

/** Tenant-scoped instrument metadata (`GET /api/v1/instruments/:instrumentId`). */
export function instrumentByIdPath(instrumentId: string): string {
  return instrumentApiPath(instrumentId)
}

export async function listCampaignInstruments(params: {
  campaignId: string
  signal?: AbortSignal
}): Promise<CampaignInstrument[]> {
  const { data: envelope } = await apiClient.get<ApiResponse<CampaignInstrument[]>>(
    `/api/v1/campaigns/${encodeURIComponent(params.campaignId)}/instruments`,
    { signal: params.signal },
  )
  return envelope?.data ?? []
}

export async function getInstrumentById(params: {
  instrumentId: string
  signal?: AbortSignal
}): Promise<CampaignInstrument> {
  const { data: envelope } = await apiClient.get<ApiResponse<CampaignInstrument>>(
    instrumentByIdPath(params.instrumentId),
    { signal: params.signal },
  )
  if (!envelope?.data?.id) {
    throw new Error('Instrument response missing data')
  }
  return envelope.data
}

export async function getInstrumentByCampaign(params: {
  campaignId: string
  instrumentId: string
  signal?: AbortSignal
}): Promise<CampaignInstrument> {
  const { data: envelope } = await apiClient.get<ApiResponse<CampaignInstrument>>(
    campaignInstrumentPath(params.campaignId, params.instrumentId),
    { signal: params.signal },
  )
  if (!envelope?.data?.id) {
    throw new Error('Instrument response missing data')
  }
  return envelope.data
}

function isRecoverableInstrumentLookupError(err: unknown): boolean {
  if (err instanceof ApiError) {
    return (
      err.isNotFound ||
      err.status === 405 ||
      err.status === 502 ||
      err.status === 503 ||
      err.status === 0
    )
  }
  return true
}

/**
 * Resolves instrument metadata for builder/routing: tenant lookup, then campaign-scoped
 * GET, then campaign instruments list (uses session cache when URL has no campaignId).
 */
export async function resolveInstrumentMetadata(params: {
  instrumentId: string
  signal?: AbortSignal
}): Promise<CampaignInstrument> {
  const { instrumentId, signal } = params
  const cached = readInstrumentWorkspaceContext(instrumentId)

  try {
    const item = await getInstrumentById({ instrumentId, signal })
    rememberInstrumentWorkspaceContext(instrumentId, {
      campaignId: item.campaignId ?? '',
      loiId: item.loiId ?? '',
    })
    return item
  } catch (error_) {
    if (!isRecoverableInstrumentLookupError(error_)) {
      throw error_
    }
    const campaignId = cached?.campaignId?.trim()
    if (!campaignId) {
      throw error_
    }

    try {
      const item = await getInstrumentByCampaign({ campaignId, instrumentId, signal })
      rememberInstrumentWorkspaceContext(instrumentId, {
        campaignId: item.campaignId ?? '',
        loiId: item.loiId ?? '',
      })
      return item
    } catch {
      /* fall through to list */
    }

    const list = await listCampaignInstruments({ campaignId, signal })
    const found = list.find((row) => row.id === instrumentId)
    if (found) {
      rememberInstrumentWorkspaceContext(instrumentId, {
        campaignId: found.campaignId ?? '',
        loiId: found.loiId ?? '',
      })
      return found
    }

    throw error_
  }
}
